import { useEffect, useState } from "react";
import { Alert, Link } from "@mui/material";
import { useAuth } from "../../hooks/AuthProvider";
import { AI_PROVIDERS_ENDPOINT, AI_SETTINGS_ENDPOINT } from "../../config/urls";
import { aiFetch, goToIntegrations } from "./aiApi";

/**
 * Shows whether the tenant's own key behind an AI feature is healthy.
 *
 * override: the feature's ai_override ({source, provider, model}) or null for the
 * tenant default. Only own-key selections can be unhealthy — BotBridge keys are ours.
 */
export default function AIConnectionHealthBanner({ override = null, refreshKey = 0, compact = false }) {
  const { getValidToken } = useAuth();
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [providersRes, settingsRes] = await Promise.all([
        aiFetch(getValidToken, AI_PROVIDERS_ENDPOINT),
        aiFetch(getValidToken, AI_SETTINGS_ENDPOINT),
      ]);
      if (!cancelled && providersRes.ok && settingsRes.ok) {
        setState({ providers: providersRes.data.providers || [], settings: settingsRes.data });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, override?.source, override?.provider]);

  if (!state) return null;

  const { providers, settings } = state;
  const usesOwnKey = override ? override.source === "own" : settings.credential_source === "own";
  const providerSlug = override ? override.provider : settings.chat_provider;
  if (!usesOwnKey || !providerSlug) return null;

  const provider = providers.find((p) => p.slug === providerSlug);
  const name = provider?.name || providerSlug;
  const connection = provider?.connection;
  const fallbackActive = settings.fallback_active_since && settings.fallback_provider === providerSlug;
  const alertSx = compact ? { py: 0, fontSize: 13 } : undefined;
  const manage = (
    <Link component="button" onClick={goToIntegrations} sx={{ ml: 0.5 }}>
      Manage AI integrations
    </Link>
  );

  if (!connection) {
    return (
      <Alert severity="error" sx={alertSx}>
        {name} is not connected with your own key.{" "}
        {settings.fallback_to_platform ? "Replies are being generated on BotBridge keys." : "AI replies will fail until it is connected."}
        {manage}
      </Alert>
    );
  }

  if (connection.status === "error") {
    return (
      <Alert severity="error" sx={alertSx}>
        <strong>{name}:</strong> {connection.last_error?.message}{" "}
        {fallbackActive
          ? `Using BotBridge keys because your ${name} key failed.`
          : settings.fallback_to_platform
            ? ""
            : "Customers get the bot's fallback reply until this is fixed."}
        {manage}
      </Alert>
    );
  }

  return null;
}
